import { cva, type VariantProps } from "class-variance-authority";
import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../../lib/utils";

const spinnerVariants = cva("border-4 rounded-full animate-spin border-t-transparent", {
  variants: {
    size: {
      xxs: "w-2 h-2 border-2",
      xs: "w-4 h-4 border-[3px]",
      sm: "w-8 h-8",
      md: "w-16 h-16",
      lg: "w-24 h-24",
    },
    color: {
      // Emphasis ramp (darkest → lightest): all derive from --foreground and step by opacity so the
      // primary → secondary → muted order holds in both light and dark modes (the token flips together).
      primary: "border-foreground",
      secondary: "border-foreground/60",
      destructive: "border-destructive",
      muted: "border-foreground/35",
      white: "border-primary-foreground",
      // No violet token exists yet in the seeded token contract — revisit when an ai/accent
      // color token is added.
      ai: "border-violet-600",
    },
  },
  defaultVariants: {
    size: "md",
    color: "primary",
  },
});

const labelVariants = cva("text-sm text-center", {
  variants: {
    color: {
      primary: "text-muted-foreground",
      secondary: "text-muted-foreground",
      destructive: "text-destructive",
      muted: "text-muted-foreground",
      white: "text-primary-foreground",
      // Matches the spinner's flagged literal violet; a lighter shade fails WCAG AA contrast.
      ai: "text-violet-600",
    },
  },
  defaultVariants: {
    color: "primary",
  },
});

export interface SpinnerProps extends VariantProps<typeof spinnerVariants> {
  /** Additional CSS classes applied to the spinning element. */
  className?: string;
  /**
   * The size of the spinner.
   * @default "md"
   */
  size?: "xxs" | "xs" | "sm" | "md" | "lg";
  /**
   * The color theme of the spinner.
   * @default "primary"
   */
  color?: "primary" | "secondary" | "destructive" | "muted" | "white" | "ai";
  /** Optional label rendered next to the spinner; its color tracks the `color` variant (faded). */
  children?: ReactNode;
  /**
   * Layout of the optional label relative to the spinner. Only applies when `children` are present.
   * @default "column"
   */
  direction?: "row" | "column";
}

/**
 * A loading indicator with an animated spinning ring, optionally labeled.
 *
 * @example
 * ```tsx
 * <Spinner />
 * <Spinner size="sm" color="secondary" />
 * <Spinner direction="row">Loading your data</Spinner>
 * ```
 */
export const Spinner = ({ size, color, className, children, direction = "column" }: SpinnerProps) => {
  const { t } = useTranslation();
  const loadingLabel = t("Loading...");

  const spinner = (
    <div className="relative" data-slot="spinner">
      <div
        className={cn(
          spinnerVariants({ size }),
          // The track is a faint full ring behind the spinning arc so the spinner reads as a ring,
          // not just a lone moving sliver. `white` (used on filled/solid surfaces) needs a translucent
          // white track; other colors sit on light surfaces and use the muted token.
          color === "white" ? "border-white/40 border-t-white/40" : "border-muted border-t-muted"
        )}
      />
      <div
        className={cn(spinnerVariants({ size, color }), "absolute top-0 left-0 border-t-transparent", className)}
        role="status"
        aria-label={loadingLabel}
      >
        <span className="sr-only">{loadingLabel}</span>
      </div>
    </div>
  );

  if (children === undefined || children === null) return spinner;

  return (
    <div className={cn("flex items-center justify-center gap-2", direction === "column" ? "flex-col" : "flex-row")}>
      {spinner}
      <span className={cn(labelVariants({ color }))}>{children}</span>
    </div>
  );
};
